import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const fill = (body, name, shop) =>
  (body || "")
    .replaceAll("{name}", name || "there")
    .replaceAll("{first}", name || "")
    .replaceAll("{shop}", shop || "our shop")
    .replace(/\s{2,}/g, " ")
    .trim();

export const ConfirmTextDialog = ({
  open,
  onOpenChange,
  to,
  message,
  firstName = "",
  title = "Review text before sending",
  sending = false,
  onSend,
  sendLabel = "Send text",
  showPresets = true,
}) => {
  const [text, setText] = useState(message || "");
  const [replies, setReplies] = useState([]);
  const [shopName, setShopName] = useState("");

  useEffect(() => {
    if (open) setText(message || "");
  }, [message, open]);

  useEffect(() => {
    if (!open || replies.length || !showPresets) return;
    api
      .get("/sms/quick-replies")
      .then(({ data }) => {
        setReplies(data.items || []);
        setShopName(data.shop_name || "");
      })
      .catch(() => {});
  }, [open, replies.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-text-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {to && (
          <div className="text-xs text-zinc-400" data-testid="confirm-text-to">
            To: <span className="font-mono-data text-zinc-200">{to}</span>
          </div>
        )}
        {showPresets && replies.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="confirm-text-presets">
            {replies.map((q, i) => (
              <button
                key={i}
                type="button"
                data-testid={`confirm-text-preset-${i}`}
                onClick={() => setText(fill(q.body, firstName, shopName))}
                className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-xs font-semibold text-indigo-200 transition-colors duration-150 hover:bg-indigo-500/20"
              >
                {q.label}
              </button>
            ))}
          </div>
        )}
        <Textarea
          data-testid="confirm-text-message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          className="mt-1 text-sm"
          placeholder="Message to the customer"
        />
        <div className="text-[11px] text-zinc-500">
          {text.length} characters ·{" "}
          {showPresets ? "pick a preset above or edit before it sends" : "edit before it sends"}
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="confirm-text-cancel"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="confirm-text-send"
            onClick={() => onSend(text.trim())}
            disabled={sending || !text.trim()}
          >
            <PaperPlaneTilt size={15} weight="bold" className="mr-1.5" />
            {sending ? "Sending…" : sendLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmTextDialog;
